import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { LANG } from '../constants/local-storage.contant';
import { Language } from '../models/language';
import { CookieUtils } from '../utils/cookie.utils';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly COOKIE_EXPIRY_DAYS = 365;
  private readonly supportedLanguages = Object.values(Language);
  private currentLanguageSubject = new BehaviorSubject<string>(Language.HY);
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(private translateService: TranslateService) {
    this.translateService.addLangs(this.supportedLanguages);
    this.translateService.setDefaultLang(Language.HY);
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    const savedLang = this.normalizeLanguage(CookieUtils.getCookie(LANG) || Language.HY);
    this.setLanguage(savedLang);
  }

  public setLanguage(lang: string): void {
    const normalizedLang = this.normalizeLanguage(lang);
    CookieUtils.setCookie(LANG, normalizedLang, { days: this.COOKIE_EXPIRY_DAYS });

    this.ensureLanguageLoaded(normalizedLang)
      .pipe(
        switchMap(() => this.translateService.use(normalizedLang).pipe(take(1))),
        catchError(() => of(normalizedLang)),
        take(1)
      )
      .subscribe(() => {
        this.applyLangToDocument(normalizedLang);
        this.currentLanguageSubject.next(normalizedLang);
      });
  }

  private applyLangToDocument(lang: Language): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('lang', lang);
    for (const supported of this.supportedLanguages) {
      document.body.classList.remove(`lang-${supported}`);
    }
    document.body.classList.add(`lang-${lang}`);
  }

  public getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  public getLanguageFromStorage(): string {
    return this.normalizeLanguage(CookieUtils.getCookie(LANG) || Language.HY);
  }

  public getLanguageObservable(): Observable<string> {
    return this.currentLanguage$;
  }

  private ensureLanguageLoaded(lang: string): Observable<void> {
    const cachedTranslation = this.translateService.translations[lang];
    if (cachedTranslation && Object.keys(cachedTranslation).length > 0) {
      return of(void 0);
    }

    return this.translateService.getTranslation(lang).pipe(
      take(1),
      map((translations) => {
        this.translateService.setTranslation(lang, translations, true);
      }),
      catchError(() => of(void 0))
    );
  }

  private normalizeLanguage(lang: string): Language {
    const normalizedLang = (lang || '').toLowerCase() as Language;
    return this.supportedLanguages.includes(normalizedLang) ? normalizedLang : Language.HY;
  }
}
